import { z } from 'zod';

import { LIMITS } from '../constants.js';

import { isoDateTimeSchema, uuidSchema } from './common.js';
import { taskSchema } from './task.js';

export const chamberMainStateSchema = z.object({
  task: taskSchema,
  segmentId: uuidSchema.nullable(),
  startedAt: isoDateTimeSchema.nullable(),
  running: z.boolean(),
  /** True when the timer is accruing time right now (weekday 09:00–18:00). */
  officeActive: z.boolean(),
});
export type ChamberMainStateDto = z.infer<typeof chamberMainStateSchema>;

export const chamberInterruptStateSchema = z.object({
  label: z.string(),
  taskId: uuidSchema.nullable(),
  segmentId: uuidSchema.nullable(),
  startedAt: isoDateTimeSchema,
  officeActive: z.boolean(),
});
export type ChamberInterruptStateDto = z.infer<typeof chamberInterruptStateSchema>;

export const chamberStateSchema = z.object({
  main: chamberMainStateSchema.nullable(),
  interrupt: chamberInterruptStateSchema.nullable(),
});
export type ChamberStateDto = z.infer<typeof chamberStateSchema>;

export const chamberTaskRequestSchema = z.object({
  taskId: uuidSchema,
});
export type ChamberTaskRequestDto = z.infer<typeof chamberTaskRequestSchema>;

export const startInterruptRequestSchema = z.object({
  label: z.string().min(1).max(LIMITS.interruptLabelMax),
});
export type StartInterruptRequestDto = z.infer<typeof startInterruptRequestSchema>;

export const promoteSegmentRequestSchema = z.object({
  taskId: uuidSchema,
});
export type PromoteSegmentRequestDto = z.infer<typeof promoteSegmentRequestSchema>;

export const fireMainResponseSchema = z.object({
  completedTask: taskSchema,
  chambered: taskSchema.nullable(),
  state: chamberStateSchema,
});
export type FireMainResponseDto = z.infer<typeof fireMainResponseSchema>;

export const ejectMainResponseSchema = z.object({
  parkedTask: taskSchema,
  chambered: taskSchema.nullable(),
  state: chamberStateSchema,
});
export type EjectMainResponseDto = z.infer<typeof ejectMainResponseSchema>;

export const activeWorkRowSchema = z.object({
  userId: uuidSchema,
  firstName: z.string(),
  lastName: z.string(),
  avatarUrl: z.string().nullable(),
  mainTaskTitle: z.string().nullable(),
  mainSegmentStartedAt: isoDateTimeSchema.nullable(),
  mainElapsedSeconds: z.number().int().nonnegative(),
  mainRunning: z.boolean(),
  mainOfficeActive: z.boolean(),
  interruptLabel: z.string().nullable(),
  interruptSegmentStartedAt: isoDateTimeSchema.nullable(),
  interruptElapsedSeconds: z.number().int().nonnegative().nullable(),
  interruptOfficeActive: z.boolean().nullable(),
});
export type ActiveWorkRowDto = z.infer<typeof activeWorkRowSchema>;

export const activeWorkResponseSchema = z.object({
  rows: z.array(activeWorkRowSchema),
});
export type ActiveWorkResponseDto = z.infer<typeof activeWorkResponseSchema>;

export const taskTimeSegmentSchema = z.object({
  startedAt: isoDateTimeSchema,
  endedAt: isoDateTimeSchema.nullable(),
  minutes: z.number().int().nonnegative(),
});
export type TaskTimeSegmentDto = z.infer<typeof taskTimeSegmentSchema>;

export const taskTimeSchema = z.object({
  actualMinutes: z.number().int().nonnegative(),
  segments: z.array(taskTimeSegmentSchema),
});
export type TaskTimeDto = z.infer<typeof taskTimeSchema>;

export const todaySessionRowSchema = z.object({
  id: uuidSchema,
  slot: z.enum(['main', 'interrupt']),
  taskId: uuidSchema.nullable(),
  taskTitle: z.string().nullable(),
  label: z.string().nullable(),
  startedAt: isoDateTimeSchema,
  endedAt: isoDateTimeSchema,
  minutes: z.number().int().nonnegative(),
});
export type TodaySessionRowDto = z.infer<typeof todaySessionRowSchema>;

export const todaySessionsResponseSchema = z.object({
  sessions: z.array(todaySessionRowSchema),
});
export type TodaySessionsResponseDto = z.infer<typeof todaySessionsResponseSchema>;
